<template>
  <div v-if="request">
    <h1>Process payment</h1>
    <v-alert :value="true" type="error">
      <strong>DO NOT PRINT OR OTHERWISE COPY THIS PAGE</strong>
      <br />
      <span
        >This page is accessible only to payment admins for taking card
        payments. Copying or otherwise distributing this page constitutes a
        security risk. Please print the PDF receipt provided instead. This is
        available on authorise page after processing.</span
      >
    </v-alert>
    <p>
      Review payment details below and update the status of the payment to
      reflect outcome
    </p>
    <v-card>
      <v-card-text>
        <h2><v-icon>money</v-icon>Payment request</h2>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Request Id</strong>
          </v-flex>
          <v-flex xs12 md7>{{ request.id }}</v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>File reference</strong>
          </v-flex>
          <v-flex xs12 md7>
            <FileZ v-if="file" :file="file" />
            <span v-if="!file">{{ request.fileid }}</span>
          </v-flex>
        </v-layout>
        <v-layout row v-if="clients">
          <v-flex xs12 md5>
            <strong>Client names</strong>
          </v-flex>
          <v-flex xs12 md7>
            <EntityZ
              v-for="entity in clients"
              :key="entity.id"
              :entity="entity"
            />
          </v-flex>
        </v-layout>
        <v-layout row v-if="subjectProperty">
          <v-flex xs12 md5>
            <strong>Subject property</strong>
          </v-flex>
          <v-flex xs12 md7>
            <span style="white-space: pre-wrap">{{
              subjectProperty.name
            }}</span>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Purpose</strong>
          </v-flex>
          <v-flex xs12 md7>
            <span v-if="purpose">{{ purpose.body }}</span>
            <span v-if="!purpose">{{ request.purposeid }}</span>
          </v-flex>
        </v-layout>
        <v-layout row v-if="request.note">
          <v-flex xs12 md5>
            <strong>Note</strong>
          </v-flex>
          <v-flex xs12 md7>{{ request.note }}</v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <v-card v-if="card && issuer">
      <v-card-text>
        <h2><v-icon>credit_card</v-icon>Card details</h2>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Card number</strong>
          </v-flex>
          <v-flex xs12 md7>{{ card.number }}</v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>CVC number</strong>
          </v-flex>
          <v-flex xs12 md7>{{ card.cvc }}</v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Card issuer</strong>
          </v-flex>
          <v-flex xs12 md7>{{ issuer.name }}</v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Expiry date</strong>
          </v-flex>
          <v-flex xs12 md7>{{ card.expire }}</v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <v-card v-if="card">
      <v-card-text>
        <h2><v-icon>account_box</v-icon>Cardholder details</h2>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Cardholder name</strong>
          </v-flex>
          <v-flex xs12 md7>{{ card.name }}</v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Cardholder address</strong>
          </v-flex>
          <v-flex xs12 md7>
            <span style="white-space: pre-wrap">{{ card.address }}</span>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <v-card v-if="card && surcharge">
      <v-card-text>
        <h2><v-icon>account_balance</v-icon>Payment details</h2>
        <v-alert :value="!card.cardType" type="warning">
          <div>
            Client has NOT indicated whether the card is a credit or debit card
            (old system).
          </div>
          <div v-if="surcharge.surchargePercent > 0">
            Client has checked a box to indicate they agree to pay the surcharge
            where applicable.
          </div>
        </v-alert>
        <v-alert :value="card.cardType" type="warning">
          <span
            >Client has chosen their card to be of the type indicated below.
            Only change the card type below if it is incorrect and the client
            has agreed for any surcharge to be taken.</span
          >
        </v-alert>
        <v-select
          v-model="cardType"
          :items="cardTypes"
          item-value="id"
          item-text="body"
          label="Card type"
          outline
          @change="changeCardType"
        />
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Amount</strong>
          </v-flex>
          <v-flex xs12 md7>{{ request.amount | toCurrency }}</v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Handling fee</strong>
            <span v-if="cardType == 'CREDIT'">&#160;(charged at 2.5%)</span>
          </v-flex>
          <v-flex xs12 md7 class="paymentItem">
            <span v-if="cardType == 'CREDIT'">{{
              surcharge.surcharge | toCurrency
            }}</span>
            <span v-if="cardType != 'CREDIT'"
              >No handling fee for debit cards</span
            >
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 md5>
            <strong>Total to be collected</strong>
          </v-flex>
          <v-flex xs12 md7 class="paymentTotal">
            <span>{{ surcharge.total | toCurrency }}</span>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
    <v-card>
      <v-card-text>
        <h2><v-icon>security</v-icon>Administrator actions</h2>
        <v-select
          v-model="status"
          :items="statuses"
          item-value="id"
          item-text="body"
          label="Status"
          outline
        />
        <v-checkbox
          v-model="print"
          label="Print receipt directly to printer"
          v-if="status == 1 && canPrint"
        />
      </v-card-text>
    </v-card>
    <v-btn @click="processPayment" style="margin: 20px" color="success">
      <v-icon>done_outline</v-icon>&#160;Update status
    </v-btn>
  </div>
</template>

<script>
import EntityZ from "@/components/link/EntityZ";
import FileZ from "@/components/link/FileZ";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Process",
  metaInfo() {
    return {
      title: "Process payment",
      titleTemplate: "%s - Payments - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { EntityZ, FileZ },
  computed: {
    surcharge() {
      // calculate surcharge
      const amount = this.request.amount;
      const surchargePercent = this.surchargePercent;
      const surcharge = (amount * surchargePercent) / 100;
      // calculate total including surcharge
      const total = amount + surcharge;
      return {
        amount,
        surchargePercent,
        surcharge,
        total
      };
    }
  },
  data() {
    return {
      request: null,
      file: null,
      clients: null,
      subjectProperty: null,
      purpose: null,
      authorise: null,
      card: null,
      issuer: null,
      staff: null,
      status: 1,
      print: true,
      canPrint: false,
      cardType: "CREDIT",
      surchargePercent: null,
      statuses: [
        { id: 1, body: "Payment taken successfully" },
        { id: 2, body: "Could not take payment from card" },
        { id: 3, body: "Cancel payment silently" }
      ],
      cardTypes: [
        { id: "CREDIT", body: "Credit" },
        { id: "DEBIT", body: "Debit" }
      ],
      valueRules: [(v) => !!v || "Value is required"]
    };
  },
  methods: {
    async fetchRequest() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/${this.$route.params.id}`
      );
      // console.log(response.data);
      this.request = response.data;
    },
    async fetchAuthorise() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/${this.$route.params.id}/authorise`
      );
      // console.log(response.data);
      this.authorise = response.data;
    },
    async fetchCard() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/${this.$route.params.id}/authorise/card/revealed`
      );
      // console.log(response.data);
      this.card = response.data;
      if (this.card.cardType) {
        this.cardType = this.card.cardType;
      }
    },
    async fetchIssuer() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/requests/${this.$route.params.id}/authorise/card/issuer`
      );
      // console.log(response.data);
      this.issuer = response.data;
    },
    async fetchFile() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.request.fileid}`
      );
      // console.log(response.data);
      this.file = response.data;
    },
    async fetchClients() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.request.fileid}/clients/false`
      );
      // console.log(response.data);
      this.clients = response.data;
    },
    async fetchSubjectProperty() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.request.fileid}/subjectProperty`
      );
      // console.log(response.data);
      this.subjectProperty = response.data;
    },
    async fetchPurpose() {
      const response = await nbmlaw.get(
        `/cors/admin/payment/purposes/${this.request.purposeid}`
      );
      // console.log(response.data);
      this.purpose = response.data;
    },
    async fetchStaff() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/entities/${this.$keycloak.tokenParsed.entityid}/staff`
      );
      // console.log(response.data);
      this.staff = response.data;
    },
    changeCardType() {
      if (this.cardType == "CREDIT") {
        this.surchargePercent = 2.5;
      } else {
        this.surchargePercent = 0;
      }
    },
    async processPayment() {
      await nbmlaw.put(
        `/cors/admin/payment/requests/${this.$route.params.id}/process`,
        {
          cardType: this.cardType,
          surcharge: this.surcharge,
          status: this.status,
          print: this.canPrint ? this.print : false
        }
      );
      // console.log(response.data);
      this.$router.push({ name: "authorise" });
    }
  },
  watch: {
    request: {
      handler() {
        if (this.request) {
          this.fetchFile();
          this.fetchClients();
          this.fetchSubjectProperty();
          this.fetchPurpose();
        }
      }
    },
    authorise: {
      handler() {
        if (this.authorise) {
          this.surchargePercent = this.authorise.surchargePercent;
        }
      }
    },
    staff: {
      handler() {
        if (this.staff && this.staff.printerid) {
          this.canPrint = true;
        }
      }
    }
  },
  created() {
    if (this.$keycloak.hasRealmRole("paymentadmin")) {
      this.fetchRequest();
      this.fetchAuthorise();
      this.fetchCard();
      this.fetchIssuer();
      this.fetchStaff();
    } else {
      this.$router.push({ name: "authorise" });
    }
  }
};
</script>

<style lang="stylus">
@media print {
  body {
    display: none;
  }
}

table.heading td {
  vertical-align: bottom;
  font-weight: bold;
  font-size: 2em;
}

.adminForm {
  background: #ddddff;
  padding: 5px;
  margin-top: 25px;
}

.surcharge {
  border: 1px solid red;
  padding: 5px;
  margin: 15px 0px;
}

.paymentDescription {
  color: gray;
}

.paymentItem {
  font-size: 0.9em;
}

.paymentTotal {
  font-weight: bold;
}

.noPrintWarning {
  border: 2px solid red;
  margin: 15px 0px;
  padding: 5px;
}

.status0 {
  color: red;
}

.status1 {
  color: orange;
}

.status2 {
  color: green;
}
</style>